import { API } from "@/config/apiBase";
import React, { useState, useEffect, useContext } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  Phone, ArrowLeft, Edit, Trash2, PhoneCall, Calendar, Clock, Flame,
  Mail, User, Save, X, Check, MessageSquare, Sparkles, Brain, FileText, 
  Wand2, Gift, Heart, Download, Crown, Cake, Plus, ChevronRight, ExternalLink,
  StickyNote, History, ChevronDown, Copy
} from "lucide-react";
import CallButton from "@/components/CallButton";
import PostCallPrompt from "@/components/PostCallPrompt";
import CopyButton from "@/components/common/CopyButton";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { toast } from "sonner";
import axios from "axios";
import { AuthContext } from "@/App";
import { format, parseISO } from "date-fns";
import { canUseAI } from "@/utils/planUtils";

// Month names for birthday display
const MONTH_NAMES = ["", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const ContactDetail = () => {
  const { contactId } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);
  
  const [contact, setContact] = useState(null);
  const [groups, setGroups] = useState([]);
  const [callLogs, setCallLogs] = useState([]);
  const [occasions, setOccasions] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [showLogCall, setShowLogCall] = useState(false);
  const [editData, setEditData] = useState({});
  const [callLog, setCallLog] = useState({ duration_minutes: 15, experience: "", mood: "happy" });
  const [healthScore, setHealthScore] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [lastCallLoggedAt, setLastCallLoggedAt] = useState(null);
  const [expandedCallId, setExpandedCallId] = useState(null);
  
  // AI Features State
  const [showAIPanel, setShowAIPanel] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiType, setAiType] = useState(null);
  const [messageOccasion, setMessageOccasion] = useState("");
  const [messageTone, setMessageTone] = useState("warm");
  const [summaryNotes, setSummaryNotes] = useState("");
  const [giftBudget, setGiftBudget] = useState("medium");
  const [giftOccasion, setGiftOccasion] = useState("");
  
  // Log Call AI Summary state
  const [logCallSummary, setLogCallSummary] = useState("");
  const [logCallSummaryLoading, setLogCallSummaryLoading] = useState(false);
  
  // Use effective_plan for AI access (handles family member inheritance)
  const hasAIAccess = canUseAI(user);

  useEffect(() => {
    fetchData();
  }, [contactId]);

  const fetchData = async () => {
    try {
      const [contactRes, groupsRes, logsRes, eventsRes] = await Promise.all([
        axios.get(`${API}/contacts/${contactId}`),
        axios.get(`${API}/groups`),
        axios.get(`${API}/calls/${contactId}`),
        axios.get(`${API}/events/contact/${contactId}`)
      ]);
      
      setContact(contactRes.data);
      setEditData(contactRes.data);
      setGroups(groupsRes.data);
      setCallLogs(logsRes.data);
      
      // Filter for upcoming occasions only (next 90 days)
      const now = new Date();
      const upcoming = eventsRes.data
        .filter(e => e.days_until !== null && e.days_until >= 0 && e.days_until <= 90)
        .sort((a, b) => (a.days_until || 0) - (b.days_until || 0))
        .slice(0, 3);
      setOccasions(upcoming);
      
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load contact");
      navigate("/dashboard");
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      // Prepare update data including birthday fields
      // Sanitize: convert empty strings to null for optional fields
      const updatePayload = {
        name: editData.name,
        phone: editData.phone?.trim() || null,
        email: editData.email?.trim() || null,
        reminder_frequency_days: editData.reminder_frequency_days,
        group_id: editData.group_id,
        relationship: editData.relationship?.trim() || null,
        notes: editData.notes?.trim() || null,
        birthday_month: editData.birthday_month,
        birthday_day: editData.birthday_day,
        birthday_year: editData.birthday_year
      };
      
      await axios.put(`${API}/contacts/${contactId}`, updatePayload);
      const res = await axios.get(`${API}/contacts/${contactId}`);
      setContact(res.data);
      setEditData(res.data);
      setIsEditing(false);
      toast.success("Contact updated!");
    } catch (error) {
      toast.error("Failed to update contact");
    }
  };

  const handleDelete = async () => {
    if (!confirm("Delete this contact? This cannot be undone.")) return;
    try {
      await axios.delete(`${API}/contacts/${contactId}`);
      toast.success("Contact deleted");
      navigate("/dashboard");
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  const handleLogCall = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/calls`, {
        contact_id: contactId,
        duration_minutes: callLog.duration_minutes,
        experience: callLog.experience,
        mood: callLog.mood
      });
      setLastCallLoggedAt(new Date());
      await fetchData();
      setShowLogCall(false);
      setCallLog({ duration_minutes: 15, experience: "", mood: "happy" });
      setLogCallSummary(""); // Reset summary
      toast.success("Call logged!");
    } catch (error) {
      console.error("Failed to log call:", error);
      toast.error("Failed to log call");
    }
  };

  // Generate AI summary for the Log Call modal
  const generateLogCallSummary = async () => {
    if (!callLog.experience?.trim() || callLog.experience.trim().length < 10) {
      toast.error("Add more notes first (at least 10 characters)");
      return;
    }
    
    setLogCallSummaryLoading(true);
    try {
      const res = await axios.post(`${API}/ai/post-call-summary`, {
        contact_id: contactId,
        call_notes: callLog.experience,
        duration_minutes: callLog.duration_minutes
      });
      
      // Extract the summary text from the response
      const summaryText = res.data?.content || res.data?.summary || res.data?.message || "";
      setLogCallSummary(summaryText);
      toast.success("Summary generated!");
    } catch (error) {
      console.error("[LogCall AI] Summary failed:", error);
      if (error.response?.status === 429) {
        toast.error("AI rate limit reached. Please try again later.");
      } else if (error.response?.status === 403) {
        toast.error("Upgrade to Plus for AI features");
      } else {
        toast.error(error.response?.data?.detail || "Failed to generate summary");
      }
    } finally {
      setLogCallSummaryLoading(false);
    }
  };

  // Format birthday for display
  const formatBirthday = () => {
    if (contact?.birthday_month && contact?.birthday_day) {
      const month = MONTH_NAMES[contact.birthday_month];
      const day = contact.birthday_day;
      if (contact.birthday_year) {
        return `${month} ${day}, ${contact.birthday_year}`;
      }
      return `${month} ${day}`;
    }
    // Fallback to ISO string if structured fields not available
    if (contact?.birthday) {
      try {
        const date = parseISO(contact.birthday);
        return format(date, "MMM d, yyyy");
      } catch {
        return contact.birthday;
      }
    }
    return null;
  };

  // Check if birthday reminder exists
  const hasBirthdayReminder = occasions.some(o => o.event_type === "birthday");

  const group = groups.find(g => g.group_id === contact?.group_id);
  
  const getStatusColor = () => {
    if (!contact) return "bg-[#6D6F7C]";
    const daysSinceCall = contact.last_called 
      ? Math.floor((Date.now() - new Date(contact.last_called).getTime()) / (1000 * 60 * 60 * 24))
      : 999;
    if (daysSinceCall <= contact.reminder_frequency_days) return "bg-[#81B29A]";
    if (daysSinceCall <= contact.reminder_frequency_days * 1.5) return "bg-[#F2CC8F]";
    return "bg-[#E07A5F]";
  };

  const handleCreateBirthdayReminder = async () => {
    // Try structured fields first, then fall back to parsing ISO string
    let month = contact?.birthday_month;
    let day = contact?.birthday_day;
    
    // If structured fields are null, try to parse from ISO string
    if (!month || !day) {
      if (contact?.birthday) {
        try {
          const date = parseISO(contact.birthday);
          month = date.getMonth() + 1; // getMonth() is 0-indexed
          day = date.getDate();
        } catch {
          // Parsing failed
        }
      }
    }
    
    if (!month || !day) {
      // Open edit mode focused on birthday
      setIsEditing(true);
      toast.info("Please add a birthday first");
      return;
    }
    
    try {
      // Create birthday event with contact's birthday
      const year = new Date().getFullYear();
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      
      await axios.post(`${API}/events`, {
        contact_id: contactId,
        event_type: "birthday",
        date: dateStr,
        recurring: true,
        reminder_days_before: 7
      });
      
      await fetchData();
      toast.success("Birthday reminder created!");
    } catch (error) {
      toast.error("Failed to create birthday reminder");
    }
  };

  // Copy to clipboard helper
  const copyToClipboard = async (text, label = "Text") => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`${label} copied!`);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = text;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success(`${label} copied!`);
    }
  };

  // AI Features (keeping existing functionality)
  const handleAIRequest = async (type) => {
    if (!hasAIAccess) {
      toast.error("Upgrade to Plus for AI features");
      return;
    }
    setAiType(type);
    setAiResult(null); // Clear previous result
    setShowAIPanel(true);
  };

  const generateAIContent = async () => {
    console.log("[AI] generateAIContent called, aiType:", aiType);
    setAiLoading(true);
    setAiResult(null); // Clear previous result
    
    try {
      let endpoint = "";
      let payload = { contact_id: contactId };
      
      switch (aiType) {
        case "message":
          endpoint = `/ai/message-writer`;
          payload = { 
            contact_id: contactId, 
            occasion: messageOccasion || null, 
            tone: messageTone || "warm",
            include_memories: true
          };
          break;
        case "prep":
          endpoint = `/ai/call-prep`;
          payload = { contact_id: contactId };
          break;
        case "summary":
          endpoint = `/ai/post-call-summary`;
          payload = { 
            contact_id: contactId, 
            call_notes: summaryNotes || "",
            duration_minutes: null
          };
          break;
        case "gift":
          endpoint = `/ai/gift-recommender`;
          payload = { 
            contact_id: contactId, 
            budget_range: giftBudget || "medium", 
            occasion: giftOccasion || null 
          };
          break;
        case "health":
          endpoint = `/ai/relationship-health`;
          payload = { contact_id: contactId };
          break;
        default:
          throw new Error("Unknown AI type");
      }
      
      console.log("[AI] Making request to:", `${API}${endpoint}`, payload);
      const res = await axios.post(`${API}${endpoint}`, payload);
      console.log("[AI] Response received:", res.data);
      setAiResult(res.data);
      
    } catch (error) {
      console.error("[AI] Request failed:", error);
      console.error("[AI] Error response:", error.response?.data);
      
      if (error.response?.status === 429) {
        toast.error("AI rate limit reached. Please try again later.");
      } else if (error.response?.status === 403) {
        toast.error("Upgrade to Plus for AI features");
      } else {
        toast.error(error.response?.data?.detail || "AI request failed. Please try again.");
      }
    } finally {
      setAiLoading(false);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-[#FDFBF7] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-4 border-[#E07A5F] border-t-transparent rounded-full"></div>
      </div>
    );
  }

  if (!contact) return null;

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-md border-b border-[#F4F1EA]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="icon" className="rounded-full" data-testid="back-button">
                  <ArrowLeft className="w-5 h-5 text-[#3D405B]" />
                </Button>
              </Link>
              <h1 className="text-xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
                Contact
              </h1>
            </div>
            
            <div className="flex items-center gap-2">
              {isEditing ? (
                <>
                  <Button variant="ghost" onClick={() => { setIsEditing(false); setEditData(contact); }} data-testid="cancel-edit">
                    <X className="w-4 h-4 mr-2" />
                    Cancel
                  </Button>
                  <Button onClick={handleSave} className="bg-[#81B29A] hover:bg-[#71A28A] text-white rounded-full" data-testid="save-edit">
                    <Save className="w-4 h-4 mr-2" />
                    Save
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setIsEditing(true)} className="border-[#F4F1EA] rounded-full" data-testid="edit-contact">
                    <Edit className="w-4 h-4 mr-2" />
                    Edit
                  </Button>
                  <Button variant="ghost" onClick={handleDelete} className="text-[#E07A5F] hover:bg-[#E07A5F]/10" data-testid="delete-contact">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Contact Card - New Layout */}
        <motion.div 
          className="bg-white rounded-3xl border border-[#F4F1EA] shadow-sm overflow-hidden"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          {/* 1) Header Section */}
          <div className="p-4 sm:p-6 border-b border-[#F4F1EA]">
            {/* Mobile: Stack layout, Desktop: Side by side */}
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              {/* Left: Avatar + Name */}
              <div className="flex items-center gap-3 sm:gap-4 min-w-0 flex-1">
                {/* Avatar */}
                <div className={`w-14 h-14 sm:w-16 sm:h-16 flex-shrink-0 rounded-2xl flex items-center justify-center text-white text-xl sm:text-2xl font-bold ${getStatusColor()}`}>
                  {contact.name.charAt(0).toUpperCase()}
                </div>
                
                {/* Name & Relationship */}
                <div className="min-w-0 flex-1">
                  {isEditing ? (
                    <Input
                      value={editData.name}
                      onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                      className="text-xl font-bold bg-white border-2 border-[#F4F1EA] rounded-xl mb-1"
                      data-testid="edit-name"
                    />
                  ) : (
                    <h2 className="text-xl sm:text-2xl font-bold text-[#3D405B] line-clamp-2 sm:truncate" style={{ fontFamily: 'Fraunces, serif' }} data-testid="contact-name">
                      {contact.name}
                    </h2>
                  )}
                  
                  <div className="flex flex-wrap items-center gap-2">
                    {isEditing ? (
                      <Select 
                        value={editData.relationship || "friend"} 
                        onValueChange={(v) => setEditData({ ...editData, relationship: v })}
                      >
                        <SelectTrigger className="w-40 h-8 text-sm border-[#F4F1EA] rounded-lg">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                          <SelectItem value="friend">Friend</SelectItem>
                          <SelectItem value="family">Family</SelectItem>
                          <SelectItem value="colleague">Colleague</SelectItem>
                          <SelectItem value="acquaintance">Acquaintance</SelectItem>
                          <SelectItem value="other">Other</SelectItem>
                        </SelectContent>
                      </Select>
                    ) : contact.relationship && (
                      <span className="text-sm text-[#6D6F7C] capitalize">{contact.relationship}</span>
                    )}
                    
                    {group && !isEditing && (
                      <Badge variant="outline" className="border-[#F4F1EA]" style={{ borderColor: group.color, color: group.color }}>
                        {group.name}
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Right: Action Buttons */}
              {!isEditing && (
                <TooltipProvider>
                  <div className="flex items-center justify-center sm:justify-end gap-2 flex-shrink-0">
                    {contact.phone && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex flex-col items-center">
                            <CallButton
                              phone={contact.phone}
                              contactId={contact.contact_id}
                              contactName={contact.name}
                              onCallComplete={() => setShowLogCall(true)}
                              variant="icon"
                              size="default"
                              className="bg-[#81B29A] hover:bg-[#71A28A] text-white rounded-full w-10 h-10 flex-shrink-0"
                              data-testid="call-button"
                            />
                            <span className="text-[10px] text-[#6D6F7C] mt-1 sm:hidden">Call</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="bg-[#3D405B] text-white rounded-lg hidden sm:block">
                          <p>Call {contact.name}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {contact.phone && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex flex-col items-center">
                            <Button
                              variant="outline"
                              size="icon"
                              className="rounded-full border-[#F4F1EA] w-10 h-10 flex-shrink-0"
                              onClick={() => window.open(`sms:${contact.phone}`, '_blank')}
                              data-testid="text-button"
                            >
                              <MessageSquare className="w-4 h-4 text-[#3D405B]" />
                            </Button>
                            <span className="text-[10px] text-[#6D6F7C] mt-1 sm:hidden">Text</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="bg-[#3D405B] text-white rounded-lg hidden sm:block">
                          <p>Send text message</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    {contact.email && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex flex-col items-center">
                            <Button
                              variant="outline"
                              size="icon"
                              className="rounded-full border-[#F4F1EA] w-10 h-10 flex-shrink-0"
                              onClick={() => window.open(`mailto:${contact.email}`, '_blank')}
                              data-testid="email-button"
                            >
                              <Mail className="w-4 h-4 text-[#3D405B]" />
                            </Button>
                            <span className="text-[10px] text-[#6D6F7C] mt-1 sm:hidden">Email</span>
                          </div>
                        </TooltipTrigger>
                        <TooltipContent className="bg-[#3D405B] text-white rounded-lg hidden sm:block">
                          <p>Send email</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex flex-col items-center">
                          <Button
                            variant="outline"
                            size="icon"
                            className="rounded-full border-[#F4F1EA] w-10 h-10 flex-shrink-0"
                            onClick={() => setShowLogCall(true)}
                            data-testid="log-call-button"
                          >
                            <PhoneCall className="w-4 h-4 text-[#E07A5F]" />
                          </Button>
                          <span className="text-[10px] text-[#6D6F7C] mt-1 sm:hidden">Log</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="bg-[#3D405B] text-white rounded-lg hidden sm:block">
                        <p>Log a call</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>
              )}
            </div>
          </div>

          {/* 2) Quick Info Section */}
          <div className="px-6 py-4 border-b border-[#F4F1EA] bg-[#FDFBF7]/50">
            <div className="grid grid-cols-2 gap-4">
              {/* Phone */}
              <div>
                <Label className="text-xs text-[#6D6F7C] uppercase tracking-wide">Phone</Label>
                {isEditing ? (
                  <Input
                    value={editData.phone || ""}
                    onChange={(e) => setEditData({ ...editData, phone: e.target.value })}
                    className="mt-1 bg-white border-[#F4F1EA] rounded-lg"
                    placeholder="Phone number"
                    data-testid="edit-phone"
                  />
                ) : (
                  <p className="text-[#3D405B] font-medium mt-1" data-testid="contact-phone">
                    {contact.phone || <span className="text-[#9CA3AF]">Not set</span>}
                  </p>
                )}
              </div>
              
              {/* Email */}
              <div>
                <Label className="text-xs text-[#6D6F7C] uppercase tracking-wide">Email</Label>
                {isEditing ? (
                  <Input
                    type="email"
                    value={editData.email || ""}
                    onChange={(e) => setEditData({ ...editData, email: e.target.value })}
                    className="mt-1 bg-white border-[#F4F1EA] rounded-lg"
                    placeholder="Email address"
                    data-testid="edit-email"
                  />
                ) : contact.email ? (
                  <Popover>
                    <PopoverTrigger asChild>
                      <button 
                        className="text-[#3D405B] font-medium mt-1 truncate max-w-full text-left hover:text-[#E07A5F] transition-colors cursor-pointer block w-full"
                        data-testid="contact-email"
                      >
                        {contact.email}
                      </button>
                    </PopoverTrigger>
                    <PopoverContent 
                      className="w-auto max-w-[90vw] p-3 bg-white border-[#F4F1EA] rounded-xl shadow-lg"
                      align="start"
                    >
                      <div className="space-y-2">
                        <p className="text-sm text-[#3D405B] break-all font-medium">{contact.email}</p>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1 text-xs rounded-lg border-[#F4F1EA] hover:bg-[#F4F1EA]"
                            onClick={() => copyToClipboard(contact.email, "Email")}
                            data-testid="copy-email-btn"
                          >
                            <Copy className="w-3 h-3 mr-1" />
                            Copy
                          </Button>
                          <Button
                            size="sm"
                            className="flex-1 text-xs rounded-lg bg-[#E07A5F] hover:bg-[#D0694E] text-white"
                            onClick={() => window.open(`mailto:${contact.email}`, '_blank')}
                            data-testid="send-email-btn"
                          >
                            <Mail className="w-3 h-3 mr-1" />
                            Send
                          </Button>
                        </div>
                      </div>
                    </PopoverContent>
                  </Popover>
                ) : (
                  <p className="text-[#9CA3AF] font-medium mt-1" data-testid="contact-email">Not set</p>
                )}
              </div>
            </div>
            
            {/* Group Assignment */}
            <div className="mt-4">
              <Label className="text-xs text-[#6D6F7C] uppercase tracking-wide">Group</Label>
              {isEditing ? (
                <Select 
                  value={editData.group_id || "none"} 
                  onValueChange={(v) => setEditData({ ...editData, group_id: v === "none" ? null : v })}
                >
                  <SelectTrigger className="mt-1 bg-white border-[#F4F1EA] rounded-lg" data-testid="edit-group">
                    <SelectValue placeholder="Select group" />
                  </SelectTrigger>
                  <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                    <SelectItem value="none">No group</SelectItem>
                    {groups.map((g) => (
                      <SelectItem key={g.group_id} value={g.group_id}>
                        <div className="flex items-center gap-2">
                          <div className="w-3 h-3 rounded-full" style={{ backgroundColor: g.color }}></div>
                          {g.name}
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : group ? (
                <div className="mt-1 flex items-center gap-2">
                  <Badge 
                    variant="outline" 
                    className="border-2" 
                    style={{ borderColor: group.color, color: group.color }}
                    data-testid="contact-group"
                  >
                    {group.name}
                  </Badge>
                </div>
              ) : (
                <p className="text-[#9CA3AF] mt-1">No group assigned</p>
              )}
            </div>
          </div>

          {/* 3) Birthday Section */}
          <div className="px-6 py-4 border-b border-[#F4F1EA]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Cake className="w-4 h-4 text-[#E07A5F]" />
                <h3 className="font-semibold text-[#3D405B]">Birthday</h3>
              </div>
            </div>
            
            {isEditing ? (
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs text-[#6D6F7C]">Month</Label>
                  <Select 
                    value={editData.birthday_month ? String(editData.birthday_month) : "none"} 
                    onValueChange={(v) => setEditData({ ...editData, birthday_month: v === "none" ? null : parseInt(v) })}
                  >
                    <SelectTrigger className="mt-1 bg-white border-[#F4F1EA] rounded-lg" data-testid="edit-birthday-month">
                      <SelectValue placeholder="Month" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                      <SelectItem value="none">Not set</SelectItem>
                      {MONTH_NAMES.slice(1).map((m, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#6D6F7C]">Day</Label>
                  <Select 
                    value={editData.birthday_day ? String(editData.birthday_day) : "none"} 
                    onValueChange={(v) => setEditData({ ...editData, birthday_day: v === "none" ? null : parseInt(v) })}
                  >
                    <SelectTrigger className="mt-1 bg-white border-[#F4F1EA] rounded-lg" data-testid="edit-birthday-day">
                      <SelectValue placeholder="Day" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#F4F1EA] rounded-xl max-h-60">
                      <SelectItem value="none">Not set</SelectItem>
                      {Array.from({ length: 31 }, (_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs text-[#6D6F7C]">Year (optional)</Label>
                  <Input
                    type="number"
                    value={editData.birthday_year || ""}
                    onChange={(e) => setEditData({ ...editData, birthday_year: e.target.value ? parseInt(e.target.value) : null })}
                    className="mt-1 bg-white border-[#F4F1EA] rounded-lg"
                    placeholder="Year"
                    min="1900"
                    max={new Date().getFullYear()}
                    data-testid="edit-birthday-year"
                  />
                </div>
              </div>
            ) : (
              <div className="flex items-center justify-between">
                <div>
                  {formatBirthday() ? (
                    <>
                      <p className="text-[#3D405B] font-medium" data-testid="birthday-display">{formatBirthday()}</p>
                      {hasBirthdayReminder && (
                        <p className="text-xs text-[#81B29A] mt-1">Birthday reminder: On</p>
                      )}
                    </>
                  ) : (
                    <p className="text-[#9CA3AF]">Not set</p>
                  )}
                </div>
                
                {formatBirthday() ? (
                  hasBirthdayReminder ? (
                    <Link to={`/occasions?contact=${contactId}`}>
                      <Button variant="outline" size="sm" className="border-[#F4F1EA] rounded-full">
                        Manage
                      </Button>
                    </Link>
                  ) : (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="border-[#F4F1EA] rounded-full"
                      onClick={handleCreateBirthdayReminder}
                      data-testid="create-birthday-reminder"
                    >
                      <Plus className="w-3 h-3 mr-1" />
                      Create reminder
                    </Button>
                  )
                ) : (
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="border-[#F4F1EA] rounded-full"
                    onClick={() => setIsEditing(true)}
                    data-testid="add-birthday"
                  >
                    <Plus className="w-3 h-3 mr-1" />
                    Add birthday
                  </Button>
                )}
              </div>
            )}
          </div>

          {/* 4) Occasions Section */}
          <div className="px-6 py-4 border-b border-[#F4F1EA]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4 text-[#F2CC8F]" />
                <h3 className="font-semibold text-[#3D405B]">Occasions</h3>
              </div>
              <Button 
                variant="ghost" 
                size="sm" 
                className="text-[#6D6F7C] hover:text-[#3D405B]"
                onClick={() => navigate(`/calendar?contact=${contactId}`)}
              >
                View all <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
            
            {occasions.length > 0 ? (
              <div className="space-y-2">
                {occasions.map((occasion) => (
                  <div 
                    key={occasion.event_id} 
                    className="flex items-center justify-between p-3 bg-[#FDFBF7] rounded-xl"
                    data-testid={`occasion-${occasion.event_id}`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{occasion.event_icon}</span>
                      <div>
                        <p className="font-medium text-[#3D405B]">{occasion.event_name}</p>
                        <p className="text-xs text-[#6D6F7C]">
                          {occasion.next_occurrence ? format(parseISO(occasion.next_occurrence), "MMM d") : occasion.date}
                          {occasion.days_until === 0 && <Badge className="ml-2 bg-[#E07A5F] text-white">Today!</Badge>}
                          {occasion.days_until > 0 && occasion.days_until <= 7 && (
                            <Badge className="ml-2 bg-[#F2CC8F] text-[#3D405B]">In {occasion.days_until} days</Badge>
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-[#9CA3AF] text-sm">No occasions yet</p>
            )}
            
            <Button 
              variant="outline" 
              size="sm" 
              className="mt-3 border-[#F4F1EA] rounded-full w-full" 
              onClick={() => navigate(`/calendar?contact=${contactId}&addOccasion=true`)}
              data-testid="add-occasion"
            >
              <Plus className="w-3 h-3 mr-1" />
              Add occasion
            </Button>
          </div>

          {/* 5) Notes Section - Always visible */}
          <div className="px-6 py-4 border-b border-[#F4F1EA]">
            <div className="flex items-center justify-between mb-3">
              <div className="flex items-center gap-2">
                <StickyNote className="w-4 h-4 text-[#81B29A]" />
                <h3 className="font-semibold text-[#3D405B]">Notes</h3>
              </div>
              {!isEditing && (
                <Button 
                  variant="ghost" 
                  size="sm" 
                  className="text-[#6D6F7C] hover:text-[#3D405B]"
                  onClick={() => setIsEditing(true)}
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              )}
            </div>
            
            {isEditing ? (
              <Textarea
                value={editData.notes || ""}
                onChange={(e) => setEditData({ ...editData, notes: e.target.value })}
                className="bg-white border-[#F4F1EA] rounded-xl"
                placeholder="Add notes about this contact..."
                rows={3}
                data-testid="edit-notes"
              />
            ) : contact.notes ? (
              <p className="text-[#6D6F7C] text-sm whitespace-pre-wrap line-clamp-3">{contact.notes}</p>
            ) : (
              <p className="text-[#9CA3AF] text-sm italic">No notes yet. Click Edit to add notes.</p>
            )}
          </div>

          {/* 6) Quick Stats (Last Called, Streak, Reminder) */}
          <div className="px-6 py-4">
            <div className="grid grid-cols-2 gap-6">
              {/* Last Contacted */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Clock className="w-4 h-4 text-[#6D6F7C]" />
                  <Label className="text-xs text-[#6D6F7C] uppercase tracking-wide">Last Called</Label>
                </div>
                <p className="text-[#3D405B] font-medium" data-testid="last-called">
                  {contact.last_called 
                    ? format(new Date(contact.last_called), "MMM d, yyyy")
                    : <span className="text-[#9CA3AF]">Never</span>
                  }
                </p>
                <div className="flex items-center gap-2 mt-2">
                  <Flame className="w-4 h-4 text-[#E07A5F]" />
                  <span className="text-sm text-[#6D6F7C]">{contact.streak} day streak</span>
                </div>
              </div>
              
              {/* Reminder Frequency */}
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Calendar className="w-4 h-4 text-[#6D6F7C]" />
                  <Label className="text-xs text-[#6D6F7C] uppercase tracking-wide">Reminder</Label>
                </div>
                {isEditing ? (
                  <Select 
                    value={String(editData.reminder_frequency_days)} 
                    onValueChange={(v) => setEditData({ ...editData, reminder_frequency_days: parseInt(v) })}
                  >
                    <SelectTrigger className="bg-white border-[#F4F1EA] rounded-lg" data-testid="edit-frequency">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                      <SelectItem value="7">Weekly</SelectItem>
                      <SelectItem value="14">Every 2 weeks</SelectItem>
                      <SelectItem value="30">Monthly</SelectItem>
                      <SelectItem value="60">Every 2 months</SelectItem>
                      <SelectItem value="90">Quarterly</SelectItem>
                    </SelectContent>
                  </Select>
                ) : (
                  <p className="text-[#3D405B] font-medium">Every {contact.reminder_frequency_days} days</p>
                )}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Call History Section - Always visible */}
        <motion.div 
          className="mt-6 bg-white rounded-3xl border border-[#F4F1EA] shadow-sm p-6"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
        >
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <History className="w-5 h-5 text-[#81B29A]" />
              <h3 className="font-semibold text-[#3D405B]">Call History</h3>
            </div>
            <Button 
              variant="outline" 
              size="sm" 
              className="border-[#F4F1EA] rounded-full"
              onClick={() => setShowLogCall(true)}
            >
              <Plus className="w-3 h-3 mr-1" />
              Log Call
            </Button>
          </div>
          
          {callLogs.length > 0 ? (
            <div className="space-y-3">
              {callLogs.slice(0, 5).map((log) => {
                const isExpanded = expandedCallId === log.log_id;
                return (
                  <button
                    key={log.log_id}
                    onClick={() => setExpandedCallId(isExpanded ? null : log.log_id)}
                    className="w-full text-left p-3 bg-[#FDFBF7] rounded-xl hover:bg-[#F4F1EA]/50 transition-colors"
                    data-testid={`call-log-${log.log_id}`}
                  >
                    {/* Header row - always visible */}
                    <div className="flex items-start justify-between">
                      <div className="flex items-start gap-3">
                        <div className="w-8 h-8 bg-[#81B29A]/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                          <PhoneCall className="w-4 h-4 text-[#81B29A]" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-medium text-[#3D405B]">
                            {format(new Date(log.called_at), "MMM d, yyyy")}
                            {log.duration_minutes && (
                              <span className="text-[#6D6F7C] font-normal ml-2">({log.duration_minutes} min)</span>
                            )}
                          </p>
                          {/* Preview text when collapsed */}
                          {!isExpanded && log.experience && (
                            <p className="text-xs text-[#6D6F7C] mt-1 line-clamp-1">{log.experience}</p>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {log.mood && (
                          <span className="text-xl">
                            {log.mood === "happy" ? "😊" : log.mood === "neutral" ? "😐" : "😔"}
                          </span>
                        )}
                        <ChevronDown 
                          className={`w-4 h-4 text-[#9CA3AF] transition-transform ${isExpanded ? 'rotate-180' : ''}`}
                        />
                      </div>
                    </div>
                    
                    {/* Expanded content */}
                    {isExpanded && (
                      <div className="mt-3 pt-3 border-t border-[#F4F1EA]">
                        {/* Duration & Mood row */}
                        <div className="flex flex-wrap gap-4 text-sm text-[#6D6F7C] mb-3">
                          {log.duration_minutes && (
                            <span className="flex items-center gap-1">
                              <Clock className="w-3 h-3" />
                              {log.duration_minutes} minutes
                            </span>
                          )}
                          {log.mood && (
                            <span className="flex items-center gap-1">
                              {log.mood === "happy" ? "😊 Great call" : log.mood === "neutral" ? "😐 Okay" : "😔 Could be better"}
                            </span>
                          )}
                        </div>
                        
                        {/* Notes */}
                        {log.experience ? (
                          <div>
                            <p className="text-xs text-[#9CA3AF] uppercase tracking-wide mb-1">Notes</p>
                            <p className="text-sm text-[#3D405B] whitespace-pre-wrap">{log.experience}</p>
                          </div>
                        ) : (
                          <p className="text-sm text-[#9CA3AF] italic">No notes recorded</p>
                        )}
                        
                        {/* AI Summary if exists */}
                        {log.ai_summary && (
                          <div className="mt-3 p-2 bg-[#E07A5F]/10 rounded-lg">
                            <p className="text-xs text-[#E07A5F] uppercase tracking-wide mb-1 flex items-center gap-1">
                              <Sparkles className="w-3 h-3" /> AI Summary
                            </p>
                            <p className="text-sm text-[#3D405B]">{log.ai_summary}</p>
                          </div>
                        )}
                      </div>
                    )}
                  </button>
                );
              })}
              {callLogs.length > 5 && (
                <p className="text-center text-sm text-[#6D6F7C]">
                  +{callLogs.length - 5} more calls
                </p>
              )}
            </div>
          ) : (
            <div className="text-center py-6">
              <PhoneCall className="w-10 h-10 text-[#F4F1EA] mx-auto mb-2" />
              <p className="text-[#9CA3AF] text-sm">No calls logged yet</p>
              <p className="text-[#9CA3AF] text-xs mt-1">Log your first call to track your connection</p>
            </div>
          )}
        </motion.div>

        {/* AI Features Panel */}
        {hasAIAccess && !isEditing && (
          <motion.div 
            className="mt-6 bg-white rounded-3xl border border-[#F4F1EA] shadow-sm p-6"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="w-5 h-5 text-[#E07A5F]" />
              <h3 className="font-semibold text-[#3D405B]">AI Assistant</h3>
              <Badge className="bg-gradient-to-r from-[#E07A5F] to-[#F2CC8F] text-white">Plus</Badge>
            </div>
            
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
              <button
                onClick={() => handleAIRequest("message")}
                className="flex flex-col items-center p-4 border border-[#F4F1EA] rounded-xl hover:border-[#E07A5F] hover:bg-[#E07A5F]/5 transition-colors text-center"
                data-testid="ai-message-btn"
              >
                <MessageSquare className="w-6 h-6 text-[#E07A5F] mb-2" />
                <span className="text-sm font-medium text-[#3D405B]">Message</span>
                <span className="text-[10px] text-[#6D6F7C] mt-1 leading-tight">Draft a thoughtful message</span>
              </button>
              <button
                onClick={() => handleAIRequest("prep")}
                className="flex flex-col items-center p-4 border border-[#F4F1EA] rounded-xl hover:border-[#81B29A] hover:bg-[#81B29A]/5 transition-colors text-center"
                data-testid="ai-prep-btn"
              >
                <FileText className="w-6 h-6 text-[#81B29A] mb-2" />
                <span className="text-sm font-medium text-[#3D405B]">Call Prep</span>
                <span className="text-[10px] text-[#6D6F7C] mt-1 leading-tight">Talking points & questions</span>
              </button>
              <button
                onClick={() => handleAIRequest("summary")}
                className="flex flex-col items-center p-4 border border-[#F4F1EA] rounded-xl hover:border-[#3D405B] hover:bg-[#3D405B]/5 transition-colors text-center"
                data-testid="ai-summary-btn"
              >
                <Brain className="w-6 h-6 text-[#3D405B] mb-2" />
                <span className="text-sm font-medium text-[#3D405B]">Summary</span>
                <span className="text-[10px] text-[#6D6F7C] mt-1 leading-tight">Post-call insights</span>
              </button>
              <button
                onClick={() => handleAIRequest("gift")}
                className="flex flex-col items-center p-4 border border-[#F4F1EA] rounded-xl hover:border-[#F2CC8F] hover:bg-[#F2CC8F]/5 transition-colors text-center"
                data-testid="ai-gift-btn"
              >
                <Gift className="w-6 h-6 text-[#F2CC8F] mb-2" />
                <span className="text-sm font-medium text-[#3D405B]">Gift Ideas</span>
                <span className="text-[10px] text-[#6D6F7C] mt-1 leading-tight">Personalized suggestions</span>
              </button>
              <button
                onClick={() => handleAIRequest("health")}
                className="flex flex-col items-center p-4 border border-[#F4F1EA] rounded-xl hover:border-[#E07A5F] hover:bg-[#E07A5F]/5 transition-colors text-center"
                data-testid="ai-health-btn"
              >
                <Heart className="w-6 h-6 text-[#E07A5F] mb-2" />
                <span className="text-sm font-medium text-[#3D405B]">Health</span>
                <span className="text-[10px] text-[#6D6F7C] mt-1 leading-tight">Relationship insights</span>
              </button>
            </div>
          </motion.div>
        )}
      </main>

      {/* Log Call Modal */}
      <Dialog open={showLogCall} onOpenChange={(open) => {
        setShowLogCall(open);
        if (!open) setLogCallSummary(""); // Reset summary when closing
      }}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA]">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Fraunces, serif' }}>Log Your Call</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleLogCall} className="space-y-4">
            <div>
              <Label>Duration (minutes)</Label>
              <Input
                type="number"
                value={callLog.duration_minutes}
                onChange={(e) => setCallLog({ ...callLog, duration_minutes: parseInt(e.target.value) || 0 })}
                className="bg-white border-[#F4F1EA] rounded-xl"
              />
            </div>
            <div>
              <Label>How did it go?</Label>
              <div className="flex gap-3 mt-2">
                {["happy", "neutral", "sad"].map((mood) => (
                  <button
                    key={mood}
                    type="button"
                    onClick={() => setCallLog({ ...callLog, mood })}
                    className={`w-12 h-12 text-2xl rounded-xl border-2 transition-all ${
                      callLog.mood === mood ? "border-[#E07A5F] bg-[#E07A5F]/10" : "border-[#F4F1EA]"
                    }`}
                  >
                    {mood === "happy" ? "😊" : mood === "neutral" ? "😐" : "😔"}
                  </button>
                ))}
              </div>
            </div>
            <div>
              <Label>Notes (optional)</Label>
              <Textarea
                value={callLog.experience}
                onChange={(e) => setCallLog({ ...callLog, experience: e.target.value })}
                placeholder="What did you talk about? Any follow-ups?"
                className="bg-white border-[#F4F1EA] rounded-xl min-h-[100px] resize-none mt-1"
                data-testid="call-notes"
              />
            </div>
            
            {/* AI Summary Section - Only visible for paid users */}
            {hasAIAccess && (
              <div className="pt-3 border-t border-[#F4F1EA]">
                <div className="flex items-center justify-between mb-2">
                  <Label className="flex items-center gap-2">
                    <Sparkles className="w-4 h-4 text-[#E07A5F]" />
                    AI Summary
                  </Label>
                  {!logCallSummary && (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={generateLogCallSummary}
                      disabled={logCallSummaryLoading || !callLog.experience?.trim() || callLog.experience.trim().length < 10}
                      className="border-[#E07A5F] text-[#E07A5F] hover:bg-[#E07A5F]/10 rounded-full text-xs"
                      data-testid="generate-summary-btn"
                    >
                      {logCallSummaryLoading ? (
                        <>
                          <Brain className="w-3 h-3 mr-1 animate-pulse" />
                          Generating...
                        </>
                      ) : (
                        <>
                          <Wand2 className="w-3 h-3 mr-1" />
                          Generate Summary
                        </>
                      )}
                    </Button>
                  )}
                </div>
                
                {!logCallSummary && (!callLog.experience?.trim() || callLog.experience.trim().length < 10) && (
                  <p className="text-xs text-[#6D6F7C]">Add at least 10 characters of notes to generate an AI summary</p>
                )}
                
                {/* Generated Summary Preview */}
                {logCallSummary && (
                  <div className="mt-2 p-3 bg-gradient-to-br from-[#E07A5F]/5 to-[#81B29A]/5 rounded-xl border border-[#E07A5F]/20">
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-xs font-medium text-[#3D405B]">AI Generated Summary</span>
                      <CopyButton 
                        text={logCallSummary}
                        label="Copy"
                        className="text-xs"
                        size="sm"
                      />
                    </div>
                    <p className="text-sm text-[#3D405B] whitespace-pre-wrap mb-3">{logCallSummary}</p>
                    
                    {/* Action buttons */}
                    <div className="flex gap-2 flex-wrap">
                      <Button
                        type="button"
                        size="sm"
                        onClick={() => {
                          // Merge summary into notes
                          const existingNotes = callLog.experience?.trim() || "";
                          const mergedNotes = existingNotes 
                            ? `AI Summary:\n${logCallSummary}\n\nNotes:\n${existingNotes}`
                            : logCallSummary;
                          setCallLog({ ...callLog, experience: mergedNotes });
                          setLogCallSummary("");
                          toast.success("Summary added to notes!");
                        }}
                        className="bg-[#81B29A] hover:bg-[#71A28A] text-white rounded-full"
                        data-testid="use-summary-btn"
                      >
                        <Check className="w-3 h-3 mr-1" />
                        Save to log
                      </Button>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => setLogCallSummary("")}
                        className="rounded-full border-[#F4F1EA]"
                      >
                        <X className="w-3 h-3 mr-1" />
                        Discard
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={generateLogCallSummary}
                        disabled={logCallSummaryLoading}
                        className="text-[#6D6F7C] rounded-full"
                      >
                        <Wand2 className="w-3 h-3 mr-1" />
                        Regenerate
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            <Button type="submit" className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full">
              Save Call Log
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* AI Panel Modal */}
      <Dialog open={showAIPanel} onOpenChange={setShowAIPanel}>
        <DialogContent className="bg-white rounded-3xl border-[#F4F1EA] max-w-lg max-h-[85vh] flex flex-col">
          <DialogHeader className="flex-shrink-0">
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Fraunces, serif' }}>
              <Sparkles className="w-5 h-5 text-[#E07A5F]" />
              {aiType === "message" && "Generate Message"}
              {aiType === "prep" && "Call Prep"}
              {aiType === "summary" && "Post-Call Summary"}
              {aiType === "gift" && "Gift Ideas"}
              {aiType === "health" && "Relationship Health"}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 overflow-y-auto flex-1 pr-2" style={{ WebkitOverflowScrolling: 'touch' }}>
            {aiType === "message" && (
              <>
                <div>
                  <Label>Occasion</Label>
                  <Input
                    value={messageOccasion}
                    onChange={(e) => setMessageOccasion(e.target.value)}
                    placeholder="e.g., birthday, congratulations, checking in"
                    className="bg-white border-[#F4F1EA] rounded-xl"
                  />
                </div>
                <div>
                  <Label>Tone</Label>
                  <Select value={messageTone} onValueChange={setMessageTone}>
                    <SelectTrigger className="bg-white border-[#F4F1EA] rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                      <SelectItem value="warm">Warm</SelectItem>
                      <SelectItem value="professional">Professional</SelectItem>
                      <SelectItem value="casual">Casual</SelectItem>
                      <SelectItem value="heartfelt">Heartfelt</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            {aiType === "summary" && (
              <>
                <div>
                  <Label>Call Notes</Label>
                  <p className="text-xs text-[#6D6F7C] mb-2">Enter notes from your call to get AI-powered insights and summary</p>
                  <Textarea
                    value={summaryNotes}
                    onChange={(e) => setSummaryNotes(e.target.value)}
                    placeholder="What did you talk about? Any important topics, follow-ups, or memorable moments..."
                    className="bg-white border-[#F4F1EA] rounded-xl min-h-[120px]"
                    rows={5}
                  />
                </div>
                {callLogs.length === 0 && !summaryNotes && (
                  <div className="p-3 bg-[#F2CC8F]/20 rounded-xl text-sm text-[#3D405B]">
                    <strong>Tip:</strong> No calls logged yet. Enter notes from your recent conversation to get insights.
                  </div>
                )}
              </>
            )}
            
            {aiType === "gift" && (
              <>
                <div>
                  <Label>Budget</Label>
                  <Select value={giftBudget} onValueChange={setGiftBudget}>
                    <SelectTrigger className="bg-white border-[#F4F1EA] rounded-xl">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white border-[#F4F1EA] rounded-xl">
                      <SelectItem value="low">Under $25</SelectItem>
                      <SelectItem value="medium">$25-$75</SelectItem>
                      <SelectItem value="high">$75+</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Occasion</Label>
                  <Input
                    value={giftOccasion}
                    onChange={(e) => setGiftOccasion(e.target.value)}
                    placeholder="e.g., birthday, holiday"
                    className="bg-white border-[#F4F1EA] rounded-xl"
                  />
                </div>
              </>
            )}
            
            {(aiType === "prep" || aiType === "health") && (
              <p className="text-sm text-[#6D6F7C]">
                {aiType === "prep" 
                  ? "AI will generate talking points and questions based on your contact's history."
                  : "AI will analyze your relationship based on call history and patterns."
                }
              </p>
            )}
            
            <Button
              onClick={generateAIContent}
              disabled={aiLoading || (aiType === "summary" && !summaryNotes?.trim())}
              className="w-full bg-[#E07A5F] hover:bg-[#D0694E] text-white rounded-full disabled:opacity-50"
            >
              {aiLoading ? (
                <span className="flex items-center gap-2">
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full"></div>
                  Generating...
                </span>
              ) : (
                <>
                  <Wand2 className="w-4 h-4 mr-2" />
                  Generate
                </>
              )}
            </Button>
            
            {aiResult && (
              <div className="mt-4 p-4 bg-[#FDFBF7] rounded-xl" data-testid="ai-result">
                {/* Relationship Health specific display */}
                {aiType === "health" && aiResult.health_score !== undefined ? (
                  <div className="space-y-4">
                    {/* Copy button for health */}
                    <div className="flex justify-end">
                      <CopyButton 
                        text={`Relationship Health: ${Math.round(aiResult.health_score)}/100\nStatus: ${aiResult.status?.replace("_", " ") || "Healthy"}\n\n${aiResult.status_message || ""}${aiResult.ai_insights ? `\n\nAI Suggestion:\n${aiResult.ai_insights}` : ""}`}
                        label="Copy"
                        className="text-xs"
                      />
                    </div>
                    
                    {/* Score and status */}
                    <div className="text-center">
                      <div className="text-4xl mb-2">{aiResult.status_emoji || "💚"}</div>
                      <div className="text-3xl font-bold text-[#3D405B]" style={{ fontFamily: 'Fraunces, serif' }}>
                        {Math.round(aiResult.health_score)}/100
                      </div>
                      <div className="text-sm text-[#6D6F7C] capitalize mt-1">
                        {aiResult.status?.replace("_", " ") || "Healthy"}
                      </div>
                    </div>
                    
                    {/* Status message */}
                    <p className="text-[#3D405B] text-center">
                      {aiResult.status_message}
                    </p>
                    
                    {/* Metrics summary */}
                    {aiResult.metrics && (
                      <div className="grid grid-cols-3 gap-2 pt-3 border-t border-[#F4F1EA]">
                        <div className="text-center">
                          <div className="text-lg font-semibold text-[#3D405B]">{aiResult.metrics.streak || 0}</div>
                          <div className="text-xs text-[#6D6F7C]">Streak</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-semibold text-[#3D405B]">{aiResult.metrics.total_calls || 0}</div>
                          <div className="text-xs text-[#6D6F7C]">Total Calls</div>
                        </div>
                        <div className="text-center">
                          <div className="text-lg font-semibold text-[#3D405B]">{aiResult.metrics.days_since_last_call ?? "-"}</div>
                          <div className="text-xs text-[#6D6F7C]">Days Ago</div>
                        </div>
                      </div>
                    )}
                    
                    {/* AI insights */}
                    {aiResult.ai_insights && (
                      <div className="pt-3 border-t border-[#F4F1EA]">
                        <div className="flex items-center justify-between mb-2">
                          <div className="text-xs font-medium text-[#6D6F7C] uppercase">AI Suggestion</div>
                          <CopyButton 
                            text={aiResult.ai_insights}
                            label="Copy"
                            className="text-xs"
                            size="sm"
                          />
                        </div>
                        <p className="text-sm text-[#3D405B]">{aiResult.ai_insights}</p>
                      </div>
                    )}
                  </div>
                ) : (
                  /* Generic display for other AI types (message, prep, summary, gift) */
                  <div className="space-y-3">
                    {/* Copy All button at top */}
                    <div className="flex justify-end">
                      <CopyButton 
                        text={(() => {
                          const mainContent = aiResult.content || aiResult.message || aiResult.prep || "";
                          const suggestions = aiResult.suggestions;
                          if (Array.isArray(suggestions)) {
                            return `${mainContent}\n\nSuggestions:\n${suggestions.map((s, i) => `${i + 1}. ${s}`).join("\n")}`;
                          }
                          return mainContent || (typeof suggestions === 'string' ? suggestions : "");
                        })()}
                        label="Copy All"
                        className="text-xs"
                      />
                    </div>
                    
                    {/* Main content */}
                    <p className="text-[#3D405B] whitespace-pre-wrap">
                      {aiResult.content || aiResult.message || aiResult.prep || aiResult.suggestions || aiResult.score || ""}
                    </p>
                    
                    {/* Individual suggestions with copy buttons */}
                    {aiResult.suggestions && Array.isArray(aiResult.suggestions) && (
                      <div className="space-y-2 pt-2 border-t border-[#F4F1EA]">
                        <div className="text-xs font-medium text-[#6D6F7C] uppercase">Suggestions</div>
                        {aiResult.suggestions.map((s, i) => (
                          <div key={i} className="flex items-start justify-between gap-2 p-2 bg-white rounded-lg">
                            <span className="text-sm text-[#3D405B] flex-1">• {s}</span>
                            <CopyButton 
                              text={s}
                              showLabel={false}
                              size="sm"
                              className="text-xs flex-shrink-0"
                            />
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Post-call prompt */}
      <PostCallPrompt
        onLogCall={() => setShowLogCall(true)}
        lastCallLoggedAt={lastCallLoggedAt}
      />
    </div>
  );
};

export default ContactDetail;
